import React, { useEffect, useState } from "react";
import { Table, Input, Button, Space, message, Select, Form, Tag, Checkbox, Popconfirm, Dropdown, Menu, Card, Avatar, Typography, Badge, Statistic, Row, Col, Tooltip, DatePicker, Modal, Popover, Pagination } from "antd";
import { 
    SearchOutlined, 
    ReloadOutlined, 
    PlusOutlined, 
    DeleteOutlined, 
    MoreOutlined,
    FilterOutlined,
    ClearOutlined,
    AlertOutlined,
    BugOutlined,
    ExclamationCircleOutlined,
    CheckCircleOutlined,
    ClockCircleOutlined,
    UserOutlined,
    CalendarOutlined,
    FileTextOutlined,
    HistoryOutlined,
    EditOutlined,
    CheckOutlined,
    FlagOutlined,
    InboxOutlined,
    EyeOutlined,
    SyncOutlined,
    CommentOutlined,
    PhoneOutlined,
    ShopOutlined,
    DollarOutlined,
    CreditCardOutlined,
    RiseOutlined,
    InfoCircleOutlined,
    FallOutlined
} from "@ant-design/icons";
import API from "../../API/API";
import AddIssueDrawer from "./AddIssueDrawer";
import IssueDetailsDrawer from "./IssueDetailsDrawer";
import { motion } from "framer-motion";
import dayjs from "dayjs";
import relativeTime from 'dayjs/plugin/relativeTime';

dayjs.extend(relativeTime);

const { Title, Text } = Typography;
const { Option } = Select;
const { RangePicker } = DatePicker;

const getIssueTypeDisplay = (issue) => {
    if (!issue) return 'Untitled Issue';
    if (issue.issueType === 'Other') {
        return issue.customIssueType?.trim() || 'Other';
    }
    return issue.issueType || 'Untitled Issue';
};

// Pure helper (module scope so it can seed useState without a hoisting issue).
const getDateRangeFromFilter = (filter) => {
    const today = dayjs();
    switch (filter) {
        case 'today':
            return [today.startOf('day'), today.endOf('day')];
        case 'yesterday': {
            const yesterday = today.subtract(1, 'day');
            return [yesterday.startOf('day'), yesterday.endOf('day')];
        }
        case 'thisWeek':
            return [today.startOf('week'), today.endOf('week')];
        case 'thisMonth':
            return [today.startOf('month'), today.endOf('month')];
        case 'lastMonth': {
            const lastMonth = today.subtract(1, 'month');
            return [lastMonth.startOf('month'), lastMonth.endOf('month')];
        }
        case 'thisQuarter':
            return [today.startOf('quarter'), today.endOf('quarter')];
        case 'thisYear':
            return [today.startOf('year'), today.endOf('year')];
        case 'custom':
            return null;
        default:
            return [today.startOf('month'), today.endOf('month')];
    }
};

// Read the lead object off an issue row. The backend now populates every lead
// field the table needs, so there is no per-row lead fetch any more.
const getLeadData = (record) =>
    record && record.lead && typeof record.lead === 'object' ? record.lead : {};

const IssueTracking = () => {
    const [issues, setIssues] = useState([]);
    const [loading, setLoading] = useState(false);
const [currentPage, setCurrentPage] = useState(1);
const [pageSize, setPageSize] = useState(10);
const [totalRecords, setTotalRecords] = useState(0);
    const [searchText, setSearchText] = useState('');
    const [sorter, setSorter] = useState({ field: 'createdAt', order: 'desc' });
    const [filters, setFilters] = useState({});
    const [drawerVisible, setDrawerVisible] = useState(false);
    const [detailsDrawerVisible, setDetailsDrawerVisible] = useState(false);
    const [selectedIssue, setSelectedIssue] = useState(null);
    const [dateRange, setDateRange] = useState(() => getDateRangeFromFilter('thisMonth'));
    const [quickFilter, setQuickFilter] = useState('thisMonth'); // Default to this month
    const [showCustomRange, setShowCustomRange] = useState(false);
    
    // Add these state variables with your other states
    const [salesAgentsMap, setSalesAgentsMap] = useState({});
    const [serviceAgentsMap, setServiceAgentsMap] = useState({});
    const [loadingAgents, setLoadingAgents] = useState(false);
    
    // Selection states
    const [selectedRowKeys, setSelectedRowKeys] = useState([]);
    const [selectAll, setSelectAll] = useState(false);

    // Statistics
    const [statistics, setStatistics] = useState({
        total: 0,
        open: 0,
        inProgress: 0,
        resolved: 0,
        critical: 0
    });
// One shared History modal serves the four logged fields. Keys match the :field
// segment of GET /api/issues/:id/field-history/:field.
// Every status the Issue schema accepts (ISSUE_STATUS in IssueTracking.js). The
// filter dropdown further down offers a shorter list on purpose; the inline
// editor has to offer all of them or a status could be set but never restored.
const ISSUE_STATUS_OPTIONS = [
    'Open',
    'In Progress',
    'Waiting for Vendor',
    'Resolved',
    'Closed',
    'Replacement',
    'Refund to cx',
    'Refund from the Yard',
    'Part in Transit',
    'Charge back',
    'Dispute'
].map((value) => ({ value, label: value }));

const FIELD_HISTORY_CONFIG = {
    issue_created_date: { title: 'Issue Created Date History', noun: 'date', isDate: true },
    customer_comments: { title: 'Customer Comments History', noun: 'comment' },
    yard_comments: { title: 'Yard Comments History', noun: 'yard comment' },
    status: { title: 'Status History', noun: 'status change' },
    calculation_notes: { title: 'Calculation Notes History', noun: 'note' },
};

// changed_by is a populated user on entries this feature wrote; changed_by_name
// is the legacy `createdBy` username the backend falls back to for issues that
// predate these logs.
const formatHistoryActor = (entry) => {
    const user = entry?.changed_by;
    if (user) {
        const name = [user.firstName, user.lastName].filter(Boolean).join(' ');
        return name || user.userName || user.email || 'Unknown';
    }
    return entry?.changed_by_name || 'Unknown';
};

// Add these with your other state variables
const [yardCommentModalVisible, setYardCommentModalVisible] = useState(false);
const [selectedIssueForYardComment, setSelectedIssueForYardComment] = useState(null);
const [newYardComment, setNewYardComment] = useState('');
const [submittingYardComment, setSubmittingYardComment] = useState(false);
    const [commentModalVisible, setCommentModalVisible] = useState(false);
    const [selectedIssueForComment, setSelectedIssueForComment] = useState(null);
    const [newComment, setNewComment] = useState('');
    const [submittingComment, setSubmittingComment] = useState(false);

    // Field History modal — one modal shared by every History button;
    // historyField says which log is open.
    const [historyField, setHistoryField] = useState(null);
    const [historyLoading, setHistoryLoading] = useState(false);
    const [historyEntries, setHistoryEntries] = useState([]);
    const [historyIssue, setHistoryIssue] = useState(null);

    // The issue whose calculation notes are open in the full-notes modal, plus
    // that modal's edit state. The draft is kept separate from the record so
    // Cancel can discard it without a refetch.
    const [calcNotesIssue, setCalcNotesIssue] = useState(null);
    const [calcNotesEditing, setCalcNotesEditing] = useState(false);
    const [calcNotesDraft, setCalcNotesDraft] = useState('');
    const [calcNotesSaving, setCalcNotesSaving] = useState(false);

    // Whether the details drawer should open on its edit form (Edit action) or
    // read-only (View action, row click).
    const [drawerOpensInEditMode, setDrawerOpensInEditMode] = useState(false);

    // Inline status editing. Only one row can be open at a time, so the row's id
    // doubles as the "is the editor open" flag.
    const [statusEditRowId, setStatusEditRowId] = useState(null);
    const [statusEditValue, setStatusEditValue] = useState(null);
    const [statusEditDate, setStatusEditDate] = useState(null);
    const [statusEditSaving, setStatusEditSaving] = useState(false);
    // Handle quick filter change
    const handleQuickFilterChange = (value) => {
        setQuickFilter(value);
        setCurrentPage(1);

        if (value === 'custom') {
            setShowCustomRange(true);
            setDateRange(null);
        } else {
            setShowCustomRange(false);
            setDateRange(getDateRangeFromFilter(value));
        }
        // The data effect below refetches when quickFilter / dateRange change.
    };

    // Handle custom range change
    const handleCustomRangeChange = (dates) => {
        setCurrentPage(1);
        if (dates && dates[0] && dates[1]) {
            setDateRange(dates);
            setQuickFilter('custom');
        } else {
            setDateRange(getDateRangeFromFilter('thisMonth'));
            setQuickFilter('thisMonth');
            setShowCustomRange(false);
        }
    };
// ---------- Field history ----------
// One handler for every log; `field` is also the endpoint's :field segment.
const handleOpenHistory = async (issue, field, e) => {
    // The whole row opens the details drawer on click, so every control added to
    // a cell has to stop the event before it gets there.
    if (e) e.stopPropagation();

    setHistoryIssue(issue);
    setHistoryField(field);
    setHistoryLoading(true);
    setHistoryEntries([]);

    try {
        const res = await API.getIssueFieldHistory(issue._id, field);
        if (res?.success) {
            setHistoryEntries(res.data || []);
        } else {
            message.error(res?.message || 'Failed to load history');
        }
    } catch (error) {
        console.error(`Error fetching ${field} history:`, error);
        message.error('Failed to load history');
    } finally {
        setHistoryLoading(false);
    }
};

const handleCloseHistory = () => {
    setHistoryField(null);
    setHistoryEntries([]);
    setHistoryIssue(null);
};

// ---------- Inline status editing ----------
const handleOpenStatusEditor = (issue, e) => {
    if (e) e.stopPropagation();
    setStatusEditRowId(issue._id);
    setStatusEditValue(issue.status);
    // Defaults to now; the picker lets the user backdate the change.
    setStatusEditDate(dayjs());
};

const handleCloseStatusEditor = () => {
    setStatusEditRowId(null);
    setStatusEditValue(null);
    setStatusEditDate(null);
};

const handleSaveInlineStatus = async (issue) => {
    if (!statusEditValue) {
        message.warning('Please select a status');
        return;
    }

    if (statusEditValue === issue.status) {
        handleCloseStatusEditor();
        return;
    }

    setStatusEditSaving(true);
    try {
        const res = await API.updateIssue(issue._id, {
            status: statusEditValue,
            // Not a schema field — the date the change is recorded under.
            status_changed_at: (statusEditDate || dayjs()).toISOString()
        });

        if (res?.success) {
            message.success('Status updated successfully');
            handleCloseStatusEditor();
            fetchIssues();
        } else {
            message.error(res?.message || 'Failed to update status');
        }
    } catch (error) {
        console.error('Error updating status inline:', error);
        message.error(error.response?.data?.message || 'Failed to update status');
    } finally {
        setStatusEditSaving(false);
    }
};

// The small History link that sits under a cell's value.
const renderHistoryLink = (issue, field) => (
    <Button
        type="link"
        size="small"
        icon={<HistoryOutlined />}
        onClick={(e) => handleOpenHistory(issue, field, e)}
        style={{ padding: 0, height: 'auto', fontSize: 11 }}
    >
        History
    </Button>
);

// Function to open yard comment modal
const handleOpenYardCommentModal = (issue, e) => {
    if (e) e.stopPropagation();
    setSelectedIssueForYardComment(issue);
    setYardCommentModalVisible(true);
    setNewYardComment('');
};

// Function to handle submitting yard comment
const handleSubmitYardComment = async () => {
    if (!newYardComment.trim()) {
        message.warning('Please enter a yard comment');
        return;
    }

    setSubmittingYardComment(true);
    try {
        // Get existing yard descriptions
        const existingYardDescriptions = selectedIssueForYardComment.yardDescription || [];
        
        // Create new yard comment object
        const newYardCommentObj = {
            comment: newYardComment.trim(),
            changedAt: new Date().toISOString()
        };
        
        // Add to existing array (newest first)
        const updatedYardDescriptions = [newYardCommentObj, ...existingYardDescriptions];
        
        // Update the issue with new yard description
        const response = await API.updateIssue(selectedIssueForYardComment._id, {
            yardDescription: updatedYardDescriptions
        });
        
        if (response.success) {
            message.success('Yard comment added successfully');
            setYardCommentModalVisible(false);
            setSelectedIssueForYardComment(null);
            setNewYardComment('');
            // Refresh the issues list
            fetchIssues();
        } else {
            message.error(response.message || 'Failed to add yard comment');
        }
    } catch (error) {
        console.error('Error adding yard comment:', error);
        message.error('Error adding yard comment');
    } finally {
        setSubmittingYardComment(false);
    }
};
    // Fetch all users to get sales agents and service agents
    const fetchAllAgents = async () => {
        setLoadingAgents(true);
        try {
            const response = await API.userGetAll();
            if (response?.success) {
                const salesMap = {};
                const serviceMap = {};
                
                (response.data || []).forEach(user => {
                    // Sales Agents
                    if (user.role === "SALES-AGENT") {
                        salesMap[user._id] = {
                            name: `${user.firstName || ''} ${user.lastName || ''}`.trim() || user.userName || user.email,
                            email: user.email,
                            phone: user.directNumber,
                            firstName: user.firstName,
                            lastName: user.lastName,
                            role: user.role
                        };
                    }
                    // Service Agents
                    if (user.role === "SERVICE-AGENT" || user.role === "SUPPORT-AGENT") {
                        serviceMap[user._id] = {
                            name: `${user.firstName || ''} ${user.lastName || ''}`.trim() || user.userName || user.email,
                            email: user.email,
                            phone: user.directNumber,
                            firstName: user.firstName,
                            lastName: user.lastName,
                            role: user.role
                        };
                    }
                });
                
                setSalesAgentsMap(salesMap);
                setServiceAgentsMap(serviceMap);
                console.log('Sales Agents Map:', salesMap);
                console.log('Service Agents Map:', serviceMap);
            }
        } catch (error) {
            console.error("Failed to fetch agents", error);
        } finally {
            setLoadingAgents(false);
        }
    };
// Fetch one page of issues from the server. All query inputs (page, size,
// search, filters, sort, date range) are read straight from state — the effect
// below calls this whenever any of them change (debounced).
const fetchIssues = async () => {
    setLoading(true);
    try {
        let effectiveDateRange = dateRange;
        if (!effectiveDateRange && quickFilter !== 'custom') {
            effectiveDateRange = getDateRangeFromFilter(quickFilter);
        }

        const apiParams = {
            page: currentPage,
            limit: pageSize,
            sortBy: sorter.field,
            order: sorter.order,
            search: searchText?.trim() || undefined,
            ...filters,
        };

        if (effectiveDateRange?.[0] && effectiveDateRange?.[1]) {
            apiParams.startDate = effectiveDateRange[0].startOf('day').toISOString();
            apiParams.endDate = effectiveDateRange[1].endOf('day').toISOString();
        }

        const res = await API.getAllIssues(apiParams);
        if (res.success) {
            setIssues(res.data || []);
            setTotalRecords(res.pagination?.total ?? (res.data || []).length);
            if (res.stats) {
                setStatistics({
                    total: res.stats.total || 0,
                    open: res.stats.open || 0,
                    inProgress: res.stats.inProgress || 0,
                    resolved: res.stats.resolved || 0,
                    critical: res.stats.critical || 0,
                });
            }
        } else {
            message.error(res.message || "Failed to fetch issues");
        }
    } catch (err) {
        console.error(err);
        message.error("Error fetching issues");
    } finally {
        setLoading(false);
    }
};

    const didMountRef = React.useRef(false);

    // One-time setup that doesn't depend on the list query.
    useEffect(() => {
        fetchAllAgents();
        fetchIssues();          // immediate first load (no debounce delay)
        didMountRef.current = true;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Single source of truth for reloading the list: refetch (debounced) whenever
    // any query input changes. The debounce covers search-as-you-type and also
    // collapses the rapid state updates from Reset / filter changes into one call.
    const filtersKey = JSON.stringify(filters);
    const dateFromKey = dateRange?.[0]?.valueOf() ?? null;
    const dateToKey = dateRange?.[1]?.valueOf() ?? null;
    useEffect(() => {
        if (!didMountRef.current) return;   // initial load handled above
        const handle = setTimeout(fetchIssues, 350);
        return () => clearTimeout(handle);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentPage, pageSize, searchText, filtersKey, sorter.field, sorter.order, dateFromKey, dateToKey, quickFilter]);

    // Helper function to get sales agent info from lead
    const getSalesAgentInfo = (lead) => {
        if (!lead) return null;
        
        // If handledBy is already populated as object
        if (lead.handledBy && typeof lead.handledBy === 'object') {
            return {
                name: `${lead.handledBy.firstName || ''} ${lead.handledBy.lastName || ''}`.trim() || lead.handledBy.userName,
                email: lead.handledBy.email,
                id: lead.handledBy._id
            };
        }
        
        // If handledBy is an ID string, look up in map
        if (lead.handledBy && typeof lead.handledBy === 'string') {
            const agent = salesAgentsMap[lead.handledBy];
            if (agent) {
                return {
                    name: agent.name,
                    email: agent.email,
                    id: lead.handledBy
                };
            }
        }
        
        return null;
    };
    
    // Function to open comment modal
    const handleOpenCommentModal = (issue, e) => {
        if (e) e.stopPropagation();
        setSelectedIssueForComment(issue);
        setCommentModalVisible(true);
        setNewComment('');
    };

    // Function to handle submitting comment
    const handleSubmitComment = async () => {
        if (!newComment.trim()) {
            message.warning('Please enter a comment');
            return;
        }

        setSubmittingComment(true);
        try {
            const existingDescriptions = selectedIssueForComment.description || [];
            const currentStatus = selectedIssueForComment.status;
            
            const newCommentObj = {
                status: currentStatus,
                comment: newComment.trim(),
                changedAt: new Date().toISOString()
            };
            
            const updatedDescriptions = [newCommentObj, ...existingDescriptions];
            
            const response = await API.updateIssue(selectedIssueForComment._id, {
                description: updatedDescriptions
            });
            
            if (response.success) {
                message.success('Comment added successfully');
                setCommentModalVisible(false);
                setNewComment('');
                fetchIssues();
            } else {
                message.error(response.message || 'Failed to add comment');
            }
        } catch (error) {
            console.error('Error adding comment:', error);
            message.error('Error adding comment');
        } finally {
            setSubmittingComment(false);
        }
    };
    
    // Helper function to get service agent info from lead
    const getServiceAgentInfo = (lead) => {
        if (!lead) return null;
        
        // If serviceHandledBy is already populated as object
        if (lead.serviceHandledBy && typeof lead.serviceHandledBy === 'object') {
            return {
                name: `${lead.serviceHandledBy.firstName || ''} ${lead.serviceHandledBy.lastName || ''}`.trim() || lead.serviceHandledBy.userName,
                email: lead.serviceHandledBy.email,
                id: lead.serviceHandledBy._id
            };
        }
        
        // If serviceHandledBy is an ID string, look up in map
        if (lead.serviceHandledBy && typeof lead.serviceHandledBy === 'string') {
            const agent = serviceAgentsMap[lead.serviceHandledBy];
            if (agent) {
                return {
                    name: agent.name,
                    email: agent.email,
                    id: lead.serviceHandledBy
                };
            }
        }
        
        return null;
    };
    
  // Pagination is handled by the standalone <Pagination> control; this only
  // reacts to column sorting.
  const handleTableChange = (_pag, _filts, sort) => {
    const newField = sort?.field || sort?.columnKey || sorter.field;
    const newOrder = sort?.order === "ascend" ? "asc"
                   : sort?.order === "descend" ? "desc"
                   : sorter.order;
    if (newField !== sorter.field || newOrder !== sorter.order) {
        setSorter({ field: newField, order: newOrder });
        setCurrentPage(1);
    }
};
// Search runs automatically (debounced) as the user types; this just makes the
// button / Enter key jump back to page 1.
const handleSearch = () => {
    setCurrentPage(1);
};

   const handleFilterChange = (key, value) => {
    setFilters(prev => {
        const next = { ...prev };
        if (value === undefined || value === null || value === '') {
            delete next[key];
        } else {
            next[key] = value;
        }
        return next;
    });
    setCurrentPage(1);
};

const handleReset = () => {
    setSearchText('');
    setFilters({});
    setSorter({ field: 'createdAt', order: 'desc' });
    setSelectedRowKeys([]);
    setSelectAll(false);
    setQuickFilter('thisMonth');
    setShowCustomRange(false);
    setCurrentPage(1);
    setDateRange(getDateRangeFromFilter('thisMonth'));
};

// After a new issue is created: clear any active search/filter/sort that could
// hide it, jump to the newest-first first page, and show it right away
// (optimistic) while the debounced effect refetches the authoritative list.
const handleIssueCreated = (createdIssue) => {
    if (createdIssue?._id) {
        setIssues(prev => [createdIssue, ...prev.filter(i => i._id !== createdIssue._id)]);
        setTotalRecords(prev => prev + 1);
    }
    setSearchText('');
    setFilters({});
    setSorter({ field: 'createdAt', order: 'desc' });
    setQuickFilter('thisMonth');
    setShowCustomRange(false);
    setDateRange(getDateRangeFromFilter('thisMonth'));
    setCurrentPage(1);
};
    // Opening the drawer read-only. The whole row uses this, and so does the
    // View action.
    const handleIssueClick = (issue) => {
        setSelectedIssue(issue);
        setDrawerOpensInEditMode(false);
        setDetailsDrawerVisible(true);
    };

    // Same drawer, straight into its edit form.
    const handleIssueEdit = (issue, e) => {
        // The row itself opens the drawer on click, so an action button has to
        // stop the event before it reaches the row handler.
        if (e) e.stopPropagation();
        setSelectedIssue(issue);
        setDrawerOpensInEditMode(true);
        setDetailsDrawerVisible(true);
    };

    const handleIssueView = (issue, e) => {
        if (e) e.stopPropagation();
        handleIssueClick(issue);
    };

    const handleOpenCalcNotes = (issue, e) => {
        // Without this the click reaches the row handler and the details drawer
        // opens behind the modal.
        if (e) e.stopPropagation();
        setCalcNotesIssue(issue);
        // An issue with no notes yet opens straight into the editor — otherwise
        // there would be no way to add the first note from here.
        setCalcNotesEditing(!issue.calculationNotes);
        setCalcNotesDraft(issue.calculationNotes || '');
    };

    const handleCloseCalcNotes = () => {
        setCalcNotesIssue(null);
        setCalcNotesEditing(false);
        setCalcNotesDraft('');
    };

    const handleEditCalcNotes = () => {
        setCalcNotesDraft(calcNotesIssue?.calculationNotes || '');
        setCalcNotesEditing(true);
    };

    const handleCancelCalcNotes = () => {
        // Discards the draft and returns to the read-only view. On an issue that
        // had no notes to begin with there is nothing to go back to, so close.
        if (!calcNotesIssue?.calculationNotes) {
            handleCloseCalcNotes();
            return;
        }
        setCalcNotesDraft(calcNotesIssue.calculationNotes);
        setCalcNotesEditing(false);
    };

    const handleSaveCalcNotes = async () => {
        if (!calcNotesIssue?._id) return;

        const next = calcNotesDraft.trim();
        if (next === (calcNotesIssue.calculationNotes || '')) {
            setCalcNotesEditing(false);
            return;
        }

        setCalcNotesSaving(true);
        try {
            const res = await API.updateIssue(calcNotesIssue._id, { calculationNotes: next });

            if (res?.success) {
                message.success('Calculation notes updated');
                // Keep the open modal in step with what was just saved, rather
                // than showing the stale table row until the list refetches.
                setCalcNotesIssue((prev) => (prev ? { ...prev, calculationNotes: next } : prev));
                setCalcNotesEditing(false);
                fetchIssues();
            } else {
                message.error(res?.message || 'Failed to update calculation notes');
            }
        } catch (error) {
            console.error('Error saving calculation notes:', error);
            message.error(error.response?.data?.message || 'Failed to update calculation notes');
        } finally {
            setCalcNotesSaving(false);
        }
    };

    const getStatusColor = (status) => {
        switch (status) {
            case 'Open': return 'error';
            case 'In Progress': return 'processing';
            case 'Waiting for Vendor': return 'warning';
            case 'Resolved': return 'success';
            case 'Closed': return 'default';
            default: return 'default';
        }
    };

    const getStatusIcon = (status) => {
        switch (status) {
            case 'Open': return <ExclamationCircleOutlined />;
            case 'In Progress': return <ClockCircleOutlined />;
            case 'Waiting for Vendor': return <InboxOutlined />;
            case 'Resolved': return <CheckCircleOutlined />;
            case 'Closed': return <CheckCircleOutlined />;
            default: return <AlertOutlined />;
        }
    };

    const getPriorityColor = (priority) => {
        switch (priority) {
            case 'Critical': return 'error';
            case 'High': return 'warning';
            case 'Medium': return 'processing';
            case 'Low': return 'success';
            default: return 'default';
        }
    };

    const getPriorityIcon = (priority) => {
        switch (priority) {
            case 'Critical': return <FlagOutlined />;
            case 'High': return <FlagOutlined />;
            case 'Medium': return <FlagOutlined />;
            case 'Low': return <FlagOutlined />;
            default: return <FlagOutlined />;
        }
    };

    // Handle individual checkbox selection
    const handleCheckboxChange = (recordId, checked) => {
        if (checked) {
            setSelectedRowKeys([...selectedRowKeys, recordId]);
        } else {
            setSelectedRowKeys(selectedRowKeys.filter(key => key !== recordId));
        }
    };

    // Handle select all on current page
    const handleSelectAll = () => {
        if (selectAll) {
            setSelectedRowKeys([]);
            setSelectAll(false);
        } else {
            const allKeysOnPage = issues.map(issue => issue._id);
            setSelectedRowKeys(allKeysOnPage);
            setSelectAll(true);
        }
    };

    // Soft delete single issue
    const handleSoftDelete = async (id) => {
        try {
            const res = await API.softDeleteIssue(id);
            if (res.success) {
                message.success("Issue moved to trash successfully");
                setSelectedRowKeys(selectedRowKeys.filter(key => key !== id));
                fetchIssues();
            } else {
                message.error(res.message || "Failed to delete issue");
            }
        } catch (err) {
            console.error(err);
            message.error("Error deleting issue");
        }
    };

    // Bulk soft delete
    const handleBulkSoftDelete = async () => {
        if (selectedRowKeys.length === 0) {
            message.warning('Please select at least one issue');
            return;
        }

        try {
            for (const id of selectedRowKeys) {
                await API.softDeleteIssue(id);
            }

            message.success(`Successfully moved ${selectedRowKeys.length} issue(s) to trash`);
            setSelectedRowKeys([]);
            setSelectAll(false);
            fetchIssues();

        } catch (err) {
            console.error(err);
            message.error("Error deleting issues");
        }
    };

    // Bulk status change
    const handleBulkStatusChange = async (status) => {
        if (selectedRowKeys.length === 0) {
            message.warning('Please select at least one issue');
            return;
        }

        try {
            message.info(`Bulk status update to ${status} would be implemented here`);
            setSelectedRowKeys([]);
            setSelectAll(false);
            fetchIssues();
        } catch (err) {
            console.error(err);
            message.error("Error updating issues");
        }
    };

    // Updated columns with enhanced design
    const columns = [
        {
            title: (
                <Checkbox
                    checked={selectAll && selectedRowKeys.length === issues.length && issues.length > 0}
                    indeterminate={selectedRowKeys.length > 0 && selectedRowKeys.length < issues.length}
                    onChange={handleSelectAll}
                />
            ),
            key: 'selection',
            width: 50,
            render: (text, record) => (
                <Checkbox
                    checked={selectedRowKeys.includes(record._id)}
                    onChange={(e) => handleCheckboxChange(record._id, e.target.checked)}
                    onClick={(e) => e.stopPropagation()}
                />
            ),
        },
        {
            title: 'Issue',
            key: 'issueType',
            width: 280,
            sorter: true,
            render: (text, record) => (
                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                    <Avatar 
                        size={40}
                        icon={<BugOutlined />}
                        style={{ 
                            background: `linear-gradient(135deg, ${getPriorityColor(record.priority) === 'error' ? '#ff4d4f' : '#1890ff'} 0%, #40a9ff 100%)`,
                            flexShrink: 0
                        }}
                    />
                    <div>
                        <Button
                            type="link"
                            style={{ padding: 0, fontSize: 14, fontWeight: 600, height: 'auto' }}
                            onClick={() => handleIssueClick(record)}
                        >
                            {getIssueTypeDisplay(record)}
                        </Button>
                        
                    </div>
                </div>
            )
        },
        // ========== LEAD DETAILS COLUMN (Replaces CUSTOMER) ==========
{
    title: 'Lead Details',
    key: 'leadDetails',
    width: 320,
    sorter: (a, b) => {
        const nameA = a.lead?.personalInfo?.fullName || '';
        const nameB = b.lead?.personalInfo?.fullName || '';
        return nameA.localeCompare(nameB);
    },
    render: (_, record) => {
        const leadId = record.lead?._id || record.lead;
        const leadDetails = getLeadData(record);

        const personalInfo = leadDetails?.personalInfo || {};
        const vehicleInfo = leadDetails?.vehicleInfo || {};
        
        const fullName = personalInfo.fullName || 'N/A';
        const phoneNumber = personalInfo.phoneNumber || 'N/A';
        const email = personalInfo.email || 'N/A';
        const make = vehicleInfo.make || 'N/A';
        const model = vehicleInfo.model || 'N/A';
        const year = vehicleInfo.year || 'N/A';
        const part = vehicleInfo.part || 'N/A';
                const vin = vehicleInfo.vin || 'N/A';

        return (
            <Tooltip title={`Lead ID: ${leadId || 'N/A'}`}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                    {/* Customer Name & Contact */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <Avatar
                            size={36}
                            icon={<UserOutlined />}
                            style={{
                                backgroundColor: fullName !== 'N/A' ? '#1890ff' : '#f0f0f0',
                                flexShrink: 0,
                            }}
                        >
                            {fullName !== 'N/A' && fullName?.[0]?.toUpperCase()}
                        </Avatar>
                        <div style={{ flex: 1 }}>
                            <Text
                                strong
                                style={{
                                    fontSize: 13,
                                    color: '#1f2937',
                                    display: 'block'
                                }}
                                ellipsis={{ tooltip: fullName }}
                            >
                                {fullName}
                            </Text>
                            {phoneNumber !== 'N/A' && (
                                <Text
                                    type="secondary"
                                    style={{ fontSize: 11, display: 'block' }}
                                    ellipsis={{ tooltip: phoneNumber }}
                                >
                                    <PhoneOutlined style={{ marginRight: 4, fontSize: 10 }} />
                                    {phoneNumber}
                                </Text>
                            )}
                            {email !== 'N/A' && (
                                <Text
                                    type="secondary"
                                    style={{ fontSize: 10, display: 'block' }}
                                    ellipsis={{ tooltip: email }}
                                >
                                    {email}
                                </Text>
                            )}
                        </div>
                    </div>
                    
                    {/* Vehicle Info */}
                    {make !== 'N/A' && (
                        <div style={{ 
                            paddingLeft: 44,
                            marginTop: 4,
                            display: 'flex',
                            flexDirection: 'column',
                            gap: 4
                        }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                                <Tag color="blue" style={{ margin: 0, fontSize: 10 }}>
                                    {year}
                                </Tag>
                                <Text strong style={{ fontSize: 12 }}>
                                    {make} {model} -
                                    <Tag color="cyan" style={{ margin: 0, width: 'fit-content', fontSize: 10 }}>
                                     {part}
                                </Tag>
                                </Text>
                            </div>
                            {vin !== 'N/A' && (
                                <Text strong style={{ fontSize: 12 }}>
                                     {vin}
                                </Text>
                            )}
                        </div>
                    )}
                </div>
            </Tooltip>
        );
    },
},

// ========== FINANCIAL COLUMN (Complete View) ==========
{
title: 'SALES MADE CHARGE',
    key: 'financial',
    width: 280,
    sorter: (a, b) => {
        const chargeA = getLeadData(a)?.saleMadeCharge || {};
        const chargeB = getLeadData(b)?.saleMadeCharge || {};
        return (chargeA.customerAmountReceived || 0) - (chargeB.customerAmountReceived || 0);
    },
    render: (_, record) => {
        const leadDetails = getLeadData(record);

        // Get sale made charge data
        const sale = leadDetails?.saleMadeCharge || {};
        const totalAmount = Number(leadDetails?.totalAmount || 0);
        const customerAmountReceived = sale.customerAmountReceived || totalAmount || 0;
        const customerCCFee = sale.customerCCFee || 0;
        const totalGP = sale.totalGP || 0;
        
        // 🔥 CRITICAL: Get yard price from yard array's finalPrice (current yard selection)
        const getYardFinalPrice = () => {
            const yards = leadDetails?.yard || [];
            if (yards.length === 0) return 0;
            
            // Find current yard (isCurrent === true) or get the last one
            const currentYard = yards.find(yard => yard.isCurrent === true) || yards[yards.length - 1];
            return currentYard?.finalPrice || 0;
        };
        
        const yardPrice = getYardFinalPrice();
        
        // Get payment method from advancePayments
        const getPaymentMethod = () => {
            if (!leadDetails?.advancePayments || leadDetails.advancePayments.length === 0) {
                return null;
            }
            const lastPayment = leadDetails.advancePayments[leadDetails.advancePayments.length - 1];
            return lastPayment?.paymentMethod;
        };
        
        const paymentMethod = getPaymentMethod();
        
        // Calculate fee badge text
        const getFeeBadge = () => {
            const threePointFivePercent = ['Card', 'Payment Link', 'Cashapp', 'Venmo', 'PayPal'];
            if (threePointFivePercent.includes(paymentMethod)) {
                return "3.5%";
            }
            if (paymentMethod === 'Pay Tomorrow') {
                return "6%";
            }
            if (paymentMethod === 'Wire Transfer') {
                return "$35";
            }
            return paymentMethod ? "0%" : null;
        };
        
        const feeBadge = getFeeBadge();
        const margin = customerAmountReceived > 0 
            ? ((totalGP / customerAmountReceived) * 100).toFixed(1)
            : "0.0";
        const isGPPositive = totalGP >= 0;
        
        // If no sale made data at all
        if (Object.keys(sale).length === 0 && customerAmountReceived === 0) {
            return (
                <div style={{ textAlign: 'center', padding: '8px' }}>
                    <Text type="secondary" style={{ fontSize: 12 }}>No financial data</Text>
                </div>
            );
        }
        
        return (
            <Card 
                size="small" 
                style={{ 
                    borderRadius: 12, 
                    background: '#fafafa', 
                    border: 'none',
                    position: 'relative'
                }} 
                bodyStyle={{ padding: '10px 12px' }}
            >
                {/* Fee Badge */}
                {feeBadge && (
                    <Tag 
                        color={paymentMethod === 'Wire Transfer' ? 'orange' : 'purple'}
                        style={{ 
                            position: 'absolute', 
                            top: 8, 
                            right: 8,
                            fontSize: 10,
                            padding: '0 6px',
                            margin: 0,
                            borderRadius: 10
                        }}
                    >
                        {feeBadge}
                    </Tag>
                )}
                
                {/* Amount Received */}
                <div style={{ marginBottom: 8 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 2 }}>
                        <DollarOutlined style={{ color: '#52c41a', fontSize: 12 }} />
                        <Text type="secondary" style={{ fontSize: 11 }}>Amount Received:</Text>
                    </div>
                    <Text strong style={{ fontSize: 15, color: '#7c3aed' }}>
                        ${customerAmountReceived.toFixed(2)}
                    </Text>
                </div>
                
                {/* Divider */}
                <div style={{ height: 1, background: '#e8e8e8', margin: '8px 0' }} />
                
                {/* Yard Price - NOW USING finalPrice from yard array */}
                <div style={{ marginBottom: 8 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 2 }}>
                        <ShopOutlined style={{ color: '#722ed1', fontSize: 12 }} />
                        <Text type="secondary" style={{ fontSize: 11 }}>Yard Price:</Text>
                    </div>
                    <Text strong style={{ fontSize: 13, color: '#dc2626' }}>
                        ${yardPrice.toFixed(2)}
                    </Text>
                    {leadDetails?.yard && leadDetails.yard.length > 0 && (
                        <Tooltip title={`From yard: ${leadDetails.yard.find(y => y.isCurrent === true)?.name || 'Current Yard'}`}>
                            <Text type="secondary" style={{ fontSize: 9, display: 'block', marginTop: 2 }}>
                                (from current yard selection)
                            </Text>
                        </Tooltip>
                    )}
                </div>
                
                {/* Divider */}
                <div style={{ height: 1, background: '#e8e8e8', margin: '8px 0' }} />
                
                {/* CC Fee */}
                <div style={{ marginBottom: 8 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 2 }}>
                        <CreditCardOutlined style={{ color: '#d97706', fontSize: 12 }} />
                        <Text type="secondary" style={{ fontSize: 11 }}>CC Fee:</Text>
                    </div>
                    <Text strong style={{ fontSize: 13, color: '#d97706' }}>
                        ${customerCCFee.toFixed(2)}
                    </Text>
                </div>
                
                {/* Divider */}
                <div style={{ height: 1, background: '#e8e8e8', margin: '8px 0' }} />
                
                {/* Total GP */}
                <div style={{ marginBottom: 6 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 2 }}>
                        {isGPPositive ? (
                            <RiseOutlined style={{ color: '#059669', fontSize: 12 }} />
                        ) : (
                            <FallOutlined style={{ color: '#dc2626', fontSize: 12 }} />
                        )}
                        <Text type="secondary" style={{ fontSize: 11 }}>Total GP:</Text>
                    </div>
                    <Tag 
                        color={isGPPositive ? "success" : "error"}
                        style={{ 
                            borderRadius: 12, 
                            padding: '2px 10px',
                            fontSize: 12,
                            fontWeight: 600,
                            marginTop: 2
                        }}
                    >
                        ${totalGP.toFixed(2)}
                    </Tag>
                </div>
                
                {/* Margin indicator */}
                {customerAmountReceived > 0 && (
                    <div style={{ 
                        marginTop: 8,
                        paddingTop: 6,
                        borderTop: '1px dashed #e8e8e8',
                        textAlign: 'center'
                    }}>
                        <Text type="secondary" style={{ fontSize: 10 }}>
                            Margin: {margin}%
                        </Text>
                        <Tooltip title={`GP: $${totalGP.toFixed(2)} / Amount: $${customerAmountReceived.toFixed(2)}`}>
                            <InfoCircleOutlined style={{ marginLeft: 4, fontSize: 10, color: '#8c8c8c' }} />
                        </Tooltip>
                    </div>
                )}
                
                {/* Payment Method Indicator */}
                {paymentMethod && (
                    <div style={{ 
                        marginTop: 6,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: 4
                    }}>
                        <CreditCardOutlined style={{ fontSize: 10, color: '#8c8c8c' }} />
                        <Text type="secondary" style={{ fontSize: 9 }}>
                            {paymentMethod}
                        </Text>
                    </div>
                )}
            </Card>
        );
    },
},
        {
            title: 'Priority',
            dataIndex: 'priority',
            key: 'priority',
            width: 110,
            sorter: true,
            render: (priority) => (
                <Tag 
                    icon={getPriorityIcon(priority)}
                    color={getPriorityColor(priority)}
                    style={{ borderRadius: 12, padding: '2px 12px', fontWeight: 500 }}
                >
                    {priority}
                </Tag>
            )
        },
        {
            title: 'Status',
            dataIndex: 'status',
            key: 'status',
            width: 190,
            sorter: true,
            // Nothing in this cell navigates. The row opens the details drawer on
            // click, so the whole status cell swallows the event here rather than
            // relying on each control inside it to remember to; Issue Details is
            // reached from the View button in the Action column.
            onCell: () => ({
                onClick: (event) => event.stopPropagation()
            }),
            render: (status, record) => (
                <Space direction="vertical" size={2}>
                    {/* The tag and the pencil are one trigger: clicking either
                        opens the status editor, so the tag is a real target
                        rather than dead space next to a small icon. */}
                    <Popover
                        open={statusEditRowId === record._id}
                        trigger="click"
                        placement="bottomLeft"
                        onOpenChange={(open) => {
                            if (open) {
                                handleOpenStatusEditor(record);
                            } else if (!statusEditSaving) {
                                handleCloseStatusEditor();
                            }
                        }}
                        title="Change Status"
                        content={
                                <div style={{ width: 260 }} onClick={(e) => e.stopPropagation()}>
                                    <div style={{ marginBottom: 8 }}>
                                        <Select
                                            value={statusEditValue}
                                            onChange={setStatusEditValue}
                                            style={{ width: '100%' }}
                                            placeholder="Select status"
                                            options={ISSUE_STATUS_OPTIONS}
                                        />
                                    </div>

                                    <div style={{ marginBottom: 12 }}>
                                        <div style={{ fontSize: 12, color: '#666', marginBottom: 4 }}>Changed On</div>
                                        <DatePicker
                                            showTime={{ format: 'HH:mm' }}
                                            format="YYYY-MM-DD HH:mm"
                                            value={statusEditDate}
                                            onChange={setStatusEditDate}
                                            allowClear={false}
                                            style={{ width: '100%' }}
                                        />
                                    </div>

                                    <Space style={{ float: 'right' }}>
                                        <Button size="small" onClick={handleCloseStatusEditor} disabled={statusEditSaving}>
                                            Cancel
                                        </Button>
                                        <Button
                                            size="small"
                                            type="primary"
                                            icon={<CheckOutlined />}
                                            loading={statusEditSaving}
                                            onClick={() => handleSaveInlineStatus(record)}
                                        >
                                            Save
                                        </Button>
                                    </Space>
                                    <div style={{ clear: 'both' }} />
                                </div>
                            }
                    >
                        <Tooltip title="Click to change status">
                            <Space size={4} style={{ cursor: 'pointer' }}>
                                <Tag
                                    icon={getStatusIcon(status)}
                                    color={getStatusColor(status)}
                                    style={{ borderRadius: 12, padding: '2px 12px', fontWeight: 500, marginInlineEnd: 0 }}
                                >
                                    {status}
                                </Tag>
                                <EditOutlined style={{ color: '#1890ff', fontSize: 12 }} />
                            </Space>
                        </Tooltip>
                    </Popover>
                    {renderHistoryLink(record, 'status')}
                </Space>
            )
        },
        {
            title: 'Reported By',
            dataIndex: 'reportedBy',
            key: 'reportedBy',
            width: 150,
            sorter: true,
            render: (name) => (
                <Space size={4}>
                    <UserOutlined style={{ color: '#1890ff' }} />
                    <Text>{name || 'N/A'}</Text>
                </Space>
            )
        },
       {
    title: 'Customer Comments',
    key: 'comments',
    width: 200,
    align: 'left',
    render: (text, record) => {
        const commentCount = record.description?.length || 0;
        const latestComment = record.description && record.description.length > 0 
            ? [...record.description].sort((a, b) => new Date(b.changedAt) - new Date(a.changedAt))[0]
            : null;
        
        // Function to truncate text to specific length
        const truncateText = (text, maxLength = 15) => {
            if (!text) return '';
            if (text.length <= maxLength) return text;
            return text.substring(0, maxLength) + '...';
        };
        
        return (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8, width: '100%' }}>
                {/* Latest Comment Section - Shows truncated comment */}
                {latestComment ? (
                    <div 
                        style={{ 
                            width: '100%',
                            cursor: 'pointer',
                            padding: '6px 10px',
                            borderRadius: '6px',
                            backgroundColor: '#f5f5f5',
                            transition: 'all 0.3s',
                            border: '1px solid #e8e8e8'
                        }}
                        onClick={(e) => handleOpenCommentModal(record, e)}
                        onMouseEnter={(e) => {
                            e.currentTarget.style.backgroundColor = '#f0f0f0';
                            e.currentTarget.style.borderColor = '#d9d9d9';
                        }}
                        onMouseLeave={(e) => {
                            e.currentTarget.style.backgroundColor = '#f5f5f5';
                            e.currentTarget.style.borderColor = '#e8e8e8';
                        }}
                    >
                        {/* Comment count badge */}
                        <div style={{ 
                            display: 'flex', 
                            justifyContent: 'space-between', 
                            alignItems: 'center', 
                            marginBottom: 4 
                        }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                                <ClockCircleOutlined style={{ fontSize: 10, color: '#8c8c8c' }} />
                                <Text type="secondary" style={{ fontSize: 10 }}>
                                    {dayjs(latestComment.changedAt).format('MM/DD')}
                                </Text>
                            </div>
                            {commentCount > 1 && (
                                <Badge 
                                    count={commentCount - 1} 
                                    size="small" 
                                    style={{ backgroundColor: '#1890ff' }}
                                />
                            )}
                        </div>
                        
                        {/* Truncated Comment Content - 10-20 characters */}
                        <Tooltip title={latestComment.comment} placement="topLeft">
                            <Text 
                                style={{ 
                                    fontSize: 12,
                                    color: '#262626',
                                    display: 'block',
                                    lineHeight: '1.4'
                                }}
                            >
                                {truncateText(latestComment.comment, 15)}
                            </Text>
                        </Tooltip>
                    </div>
                ) : (
                    <div 
                        style={{ 
                            width: '100%',
                            cursor: 'pointer',
                            padding: '6px 10px',
                            borderRadius: '6px',
                            backgroundColor: '#fafafa',
                            border: '1px dashed #d9d9d9',
                            textAlign: 'center'
                        }}
                        onClick={(e) => handleOpenCommentModal(record, e)}
                    >
                        <Text type="secondary" style={{ fontSize: 12 }}>
                            No comments
                        </Text>
                    </div>
                )}
         
                {renderHistoryLink(record, 'customer_comments')}
            </div>
        );
    },
},
     // Add this column right after the CUSTOMER COMMENTS column in your columns array

{
    title: 'Yard Comments',
    key: 'yardComments',
    width: 200,
    align: 'left',
    render: (text, record) => {
        const yardCommentCount = record.yardDescription?.length || 0;
        const latestYardComment = record.yardDescription && record.yardDescription.length > 0 
            ? [...record.yardDescription].sort((a, b) => new Date(b.changedAt) - new Date(a.changedAt))[0]
            : null;
        
        // Function to truncate text to specific length
        const truncateText = (text, maxLength = 15) => {
            if (!text) return '';
            if (text.length <= maxLength) return text;
            return text.substring(0, maxLength) + '...';
        };
        
        return (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8, width: '100%' }}>
                {/* Latest Yard Comment Section - Shows truncated comment */}
                {latestYardComment ? (
                    <div 
                        style={{ 
                            width: '100%',
                            cursor: 'pointer',
                            padding: '6px 10px',
                            borderRadius: '6px',
                            backgroundColor: '#fff7e6',
                            transition: 'all 0.3s',
                            border: '1px solid #ffd591'
                        }}
                        onClick={(e) => {
                            e.stopPropagation();
                            // Open yard comments modal - you can create a similar modal
                            handleOpenYardCommentModal(record, e);
                        }}
                        onMouseEnter={(e) => {
                            e.currentTarget.style.backgroundColor = '#ffe7ba';
                            e.currentTarget.style.borderColor = '#ffa940';
                        }}
                        onMouseLeave={(e) => {
                            e.currentTarget.style.backgroundColor = '#fff7e6';
                            e.currentTarget.style.borderColor = '#ffd591';
                        }}
                    >
                        {/* Comment count badge */}
                        <div style={{ 
                            display: 'flex', 
                            justifyContent: 'space-between', 
                            alignItems: 'center', 
                            marginBottom: 4 
                        }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                                <ClockCircleOutlined style={{ fontSize: 10, color: '#8c8c8c' }} />
                                <Text type="secondary" style={{ fontSize: 10 }}>
                                    {dayjs(latestYardComment.changedAt).format('MM/DD')}
                                </Text>
                            </div>
                            {yardCommentCount > 1 && (
                                <Badge 
                                    count={yardCommentCount - 1} 
                                    size="small" 
                                    style={{ backgroundColor: '#fa8c16' }}
                                />
                            )}
                        </div>
                        
                        {/* Truncated Comment Content - 15 characters */}
                        <Tooltip title={latestYardComment.comment} placement="topLeft">
                            <Text 
                                style={{ 
                                    fontSize: 12,
                                    color: '#262626',
                                    display: 'block',
                                    lineHeight: '1.4'
                                }}
                            >
                                {truncateText(latestYardComment.comment, 15)}
                            </Text>
                        </Tooltip>
                    </div>
                ) : (
                    <div 
                        style={{ 
                            width: '100%',
                            cursor: 'pointer',
                            padding: '6px 10px',
                            borderRadius: '6px',
                            backgroundColor: '#fafafa',
                            border: '1px dashed #d9d9d9',
                            textAlign: 'center'
                        }}
                        onClick={(e) => {
                            e.stopPropagation();
                            handleOpenYardCommentModal(record, e);
                        }}
                    >
                        <Text type="secondary" style={{ fontSize: 12 }}>
                            No yard comments
                        </Text>
                    </div>
                )}
                
             
                {renderHistoryLink(record, 'yard_comments')}
            </div>
        );
    },
},   {
            title: 'Issue Created Date',
            key: 'issueCreatedDate',
            width: 210,
            // issue_created_date is what the user sets; issues written before that
            // field existed fall back to createdAt, which is what this column has
            // always shown.
            sorter: (a, b) =>
                new Date(a.issue_created_date || a.createdAt) - new Date(b.issue_created_date || b.createdAt),
            render: (_, record) => {
                const created = record.issue_created_date || record.createdAt;

                return (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                        <Tooltip
                            title={
                                <>
                                    <div><strong>Issue created:</strong> {created ? dayjs(created).format('MM-DD-YYYY hh:mm A') : 'N/A'}</div>
                                    <div><strong>Last updated:</strong> {record.updatedAt ? dayjs(record.updatedAt).format('MM-DD-YYYY hh:mm A') : 'N/A'}</div>
                                </>
                            }
                        >
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                                {/* Issue created date */}
                                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                    <div style={{
                                        width: 28, height: 28, borderRadius: 8,
                                        background: '#F1EFE8',
                                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                                        flexShrink: 0,
                                    }}>
                                        <ClockCircleOutlined style={{ fontSize: 13, color: '#888780' }} />
                                    </div>
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                                        <Text style={{ fontSize: 12, fontWeight: 500, lineHeight: 1.2 }}>
                                            {created ? dayjs(created).format('MM/DD/YY · hh:mm A') : 'N/A'}
                                        </Text>
                                        <Text type="secondary" style={{ fontSize: 11, lineHeight: 1.2 }}>
                                            Created · {created ? dayjs(created).fromNow() : '—'}
                                        </Text>
                                    </div>
                                </div>

                                {/* Updated */}
                                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                    <div style={{
                                        width: 28, height: 28, borderRadius: 8,
                                        background: '#E6F1FB',
                                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                                        flexShrink: 0,
                                    }}>
                                        <SyncOutlined style={{ fontSize: 13, color: '#185FA5' }} />
                                    </div>
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                                        <Text style={{ fontSize: 12, fontWeight: 500, lineHeight: 1.2, color: '#185FA5' }}>
                                            {record.updatedAt ? dayjs(record.updatedAt).format('MM/DD/YY · hh:mm A') : 'N/A'}
                                        </Text>
                                        <Text type="secondary" style={{ fontSize: 11, lineHeight: 1.2 }}>
                                            Updated · {record.updatedAt ? dayjs(record.updatedAt).fromNow() : '—'}
                                        </Text>
                                    </div>
                                </div>
                            </div>
                        </Tooltip>

                        {renderHistoryLink(record, 'issue_created_date')}
                    </div>
                );
            },
        },
        {
            title: 'Calculation Notes',
            dataIndex: 'calculationNotes',
            key: 'calculationNotes',
            width: 260,
            render: (text, record) => {
                // Single-line preview: first line only, then hard-truncated. The
                // full text lives in the modal — a popover could not show a long
                // working-out legibly inside a 260px cell.
                const firstLine = text ? text.split('\n')[0].trim() : '';
                const preview = firstLine.length > 45 ? `${firstLine.slice(0, 45)}...` : firstLine;
                const lineCount = text ? text.split('\n').filter((line) => line.trim()).length : 0;

                return (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 2, alignItems: 'flex-start' }}>
                        {text ? (
                            <Text
                                style={{
                                    fontSize: 12,
                                    color: '#8c8c8c',
                                    whiteSpace: 'nowrap',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    maxWidth: '100%'
                                }}
                            >
                                📝 {preview}
                            </Text>
                        ) : (
                            <Text type="secondary">—</Text>
                        )}

                        <Button
                            type="link"
                            size="small"
                            icon={<FileTextOutlined />}
                            onClick={(e) => handleOpenCalcNotes(record, e)}
                            style={{ padding: 0, height: 'auto', fontSize: 11 }}
                        >
                            {text
                                ? `View Full Notes${lineCount > 1 ? ` (${lineCount} lines)` : ''}`
                                : 'Add Notes'}
                        </Button>

                        {renderHistoryLink(record, 'calculation_notes')}
                    </div>
                );
            },
        },
        {
            title: 'Action',
            key: 'actions',
            width: 190,
            align: 'left',
            // Pinned to the right edge. The table is far wider than the viewport,
            // so unpinned this column could only be reached by scrolling sideways
            // past ten others — which, with no header text on it either, is why
            // it read as missing.
            fixed: 'right',
            render: (text, record) => (
                <Space size={4}>
                    <Button
                        type="link"
                        size="small"
                        icon={<EyeOutlined />}
                        onClick={(e) => handleIssueView(record, e)}
                        style={{ padding: '0 4px' }}
                    >
                        View
                    </Button>
                    <Button
                        type="link"
                        size="small"
                        icon={<EditOutlined />}
                        onClick={(e) => handleIssueEdit(record, e)}
                        style={{ padding: '0 4px' }}
                    >
                        Edit
                    </Button>
                    <Popconfirm
                        title="Move to trash?"
                        description="This issue will be moved to trash. You can restore it later."
                        onConfirm={() => handleSoftDelete(record._id)}
                        okText="Yes"
                        cancelText="No"
                    >
                        <Button
                            type="link"
                            danger
                            size="small"
                            icon={<DeleteOutlined />}
                            onClick={(e) => e.stopPropagation()}
                            style={{ padding: '0 4px' }}
                        >
                            Delete
                        </Button>
                    </Popconfirm>
                </Space>
            ),
        }
    ];

    // With scroll.x set, antd lays the table out with table-layout: fixed, so the
    // scroll width has to be at least the sum of the column widths. Short of that
    // the columns are squeezed into each other; derived here so adding or
    // resizing a column can't leave the two out of step.
    const tableScrollWidth = columns.reduce((sum, column) => sum + (column.width || 0), 0);

    // Bulk actions menu
    const bulkActionsMenu = (
        <Menu>
            <Menu.Item key="mark-open" onClick={() => handleBulkStatusChange('Open')}>
                <ExclamationCircleOutlined /> Mark as Open
            </Menu.Item>
            <Menu.Item key="mark-in-progress" onClick={() => handleBulkStatusChange('In Progress')}>
                <ClockCircleOutlined /> Mark as In Progress
            </Menu.Item>
            <Menu.Item key="mark-resolved" onClick={() => handleBulkStatusChange('Resolved')}>
                <CheckCircleOutlined /> Mark as Resolved
            </Menu.Item>
            <Menu.Item key="mark-closed" onClick={() => handleBulkStatusChange('Closed')}>
                <CheckCircleOutlined /> Mark as Closed
            </Menu.Item>
            <Menu.Divider />
            <Menu.Item key="delete" danger onClick={handleBulkSoftDelete}>
                <DeleteOutlined /> Move to Trash
            </Menu.Item>
        </Menu>
    );

    return (
        <div style={{ 
            padding: 24, 
            minHeight: "100vh", 
            background: `linear-gradient(135deg, #f5f7fa 0%, #ffffff 100%)`
        }}>
            {/* Modern Header with Stats */}
            <motion.div
                initial={{ opacity: 0, y: -20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
            >
                <Card 
                    style={{ 
                        marginBottom: 24, 
                        borderRadius: 24,
                        background: `linear-gradient(135deg, #1890ff 0%, #722ed1 100%)`,
                        border: 'none'
                    }}
                    bodyStyle={{ padding: '32px' }}
                >
                    <Row gutter={[24, 24]} align="middle">
                        <Col xs={24} md={12}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 20 }}>
                                <Avatar 
                                    size={72} 
                                    style={{ 
                                        background: 'rgba(255,255,255,0.2)',
                                        backdropFilter: 'blur(10px)',
                                        boxShadow: '0 8px 24px rgba(0,0,0,0.1)'
                                    }}
                                    icon={<BugOutlined style={{ fontSize: 36 }} />}
                                />
                                <div>
                                    <Title level={2} style={{ margin: 0, color: 'white', fontWeight: 700 }}>
                                        Issue Tracking
                                    </Title>
                                    <Text style={{ color: 'rgba(255,255,255,0.85)', fontSize: 14 }}>
                                        Track and manage all issues and bugs
                                    </Text>
                                </div>
                            </div>
                        </Col>
                        <Col xs={24} md={12}>
                            <Row gutter={[16, 16]}>
                                <Col span={6}>
                                    <Card 
                                        style={{ 
                                            background: 'rgba(255,255,255,0.15)',
                                            backdropFilter: 'blur(10px)',
                                            borderRadius: 16,
                                            border: 'none'
                                        }}
                                        bodyStyle={{ padding: '12px', textAlign: 'center' }}
                                    >
                                        <Statistic 
                                            title={<Text style={{ color: 'white', fontSize: 12 }}>Total Issues</Text>}
                                            value={statistics.total}
                                            valueStyle={{ color: 'white', fontSize: 24, fontWeight: 'bold' }}
                                        />
                                    </Card>
                                </Col>
                                <Col span={6}>
                                    <Card 
                                        style={{ 
                                            background: 'rgba(255,255,255,0.15)',
                                            backdropFilter: 'blur(10px)',
                                            borderRadius: 16,
                                            border: 'none'
                                        }}
                                        bodyStyle={{ padding: '12px', textAlign: 'center' }}
                                    >
                                        <Statistic 
                                            title={<Text style={{ color: 'white', fontSize: 12 }}>Open</Text>}
                                            value={statistics.open}
                                            valueStyle={{ color: '#ff4d4f', fontSize: 24, fontWeight: 'bold' }}
                                        />
                                    </Card>
                                </Col>
                                <Col span={6}>
                                    <Card 
                                        style={{ 
                                            background: 'rgba(255,255,255,0.15)',
                                            backdropFilter: 'blur(10px)',
                                            borderRadius: 16,
                                            border: 'none'
                                        }}
                                        bodyStyle={{ padding: '12px', textAlign: 'center' }}
                                    >
                                        <Statistic 
                                            title={<Text style={{ color: 'white', fontSize: 12 }}>In Progress</Text>}
                                            value={statistics.inProgress}
                                            valueStyle={{ color: '#faad14', fontSize: 24, fontWeight: 'bold' }}
                                        />
                                    </Card>
                                </Col>
                                <Col span={6}>
                                    <Card 
                                        style={{ 
                                            background: 'rgba(255,255,255,0.15)',
                                            backdropFilter: 'blur(10px)',
                                            borderRadius: 16,
                                            border: 'none'
                                        }}
                                        bodyStyle={{ padding: '12px', textAlign: 'center' }}
                                    >
                                        <Statistic 
                                            title={<Text style={{ color: 'white', fontSize: 12 }}>Critical</Text>}
                                            value={statistics.critical}
                                            valueStyle={{ color: '#ffa940', fontSize: 24, fontWeight: 'bold' }}
                                        />
                                    </Card>
                                </Col>
                            </Row>
                        </Col>
                    </Row>
                </Card>
            </motion.div>

            {/* Filter Section */}
            <motion.div
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 }}
            >
                <Card 
                    bordered={false}
                    style={{ 
                        borderRadius: 20,
                        boxShadow: '0 4px 12px rgba(0,0,0,0.05)',
                        marginBottom: 24,
                        border: '1px solid #f0f0f0'
                    }}
                    bodyStyle={{ padding: '20px 24px' }}
                >
                    <Row gutter={[16, 16]} align="middle">
                        <Col xs={24} md={5}>
                            <Input
                                placeholder="Search by name, email, phone or order ID..."
                                prefix={<SearchOutlined style={{ color: '#bfbfbf' }} />}
                                value={searchText}
                                onChange={(e) => { setSearchText(e.target.value); setCurrentPage(1); }}
                                onPressEnter={handleSearch}
                                allowClear
                                size="large"
                                style={{ borderRadius: 12 }}
                            />
                        </Col>
                        <Col xs={24} md={3}>
                            <Select
                                placeholder="Priority"
                                style={{ width: '100%', borderRadius: 12 }}
                                onChange={(value) => handleFilterChange('priority', value)}
                                allowClear
                                size="large"
                            >
                                <Option value="Critical">Critical</Option>
                                <Option value="High">High</Option>
                                <Option value="Medium">Medium</Option>
                                <Option value="Low">Low</Option>
                            </Select>
                        </Col>
                        <Col xs={24} md={3}>
                            <Select
                                placeholder="Status"
                                style={{ width: '100%', borderRadius: 12 }}
                                onChange={(value) => handleFilterChange('status', value)}
                                allowClear
                                size="large"
                            >
                                <Option value="Open">Open</Option>
                                <Option value="In Progress">In Progress</Option>
                                <Option value="Waiting for Vendor">Waiting for Vendor</Option>
                                <Option value="Resolved">Resolved</Option>
                                <Option value="Closed">Closed</Option>
                            </Select>
                        </Col>
                        <Col xs={24} md={5}>
                            <Select
                                placeholder="Quick Date Filter"
                                style={{ width: '100%', borderRadius: 12 }}
                                onChange={handleQuickFilterChange}
                                value={quickFilter}
                                size="large"
                                suffixIcon={<CalendarOutlined />}
                            >
                                <Option value="today">Today</Option>
                                <Option value="yesterday">Yesterday</Option>
                                <Option value="thisWeek">This Week</Option>
                                <Option value="thisMonth">This Month</Option>
                                <Option value="lastMonth">Last Month</Option>
                                <Option value="thisQuarter">This Quarter</Option>
                                <Option value="thisYear">This Year</Option>
                                <Option value="custom">Custom Range</Option>
                            </Select>
                        </Col>
                        {showCustomRange && (
                            <Col xs={24} md={5}>
                                <RangePicker
                                    style={{ width: '100%', borderRadius: 12 }}
                                    onChange={handleCustomRangeChange}
                                    value={dateRange}
                                    size="large"
                                    placeholder={['Start Date', 'End Date']}
                                />
                            </Col>
                        )}
                        <Col xs={24} md={showCustomRange ? 3 : 8}>
                            <Space style={{ width: '100%', justifyContent: 'flex-end' }}>
                                <Tooltip title="Search">
                                    <Button 
                                        type="primary"
                                        icon={<SearchOutlined />} 
                                        onClick={handleSearch}
                                        size="large"
                                        style={{ borderRadius: 12 }}
                                    >
                                        Search
                                    </Button>
                                </Tooltip>
                                <Tooltip title="Reset Filters">
                                    <Button 
                                        icon={<ClearOutlined />} 
                                        onClick={handleReset}
                                        size="large"
                                        style={{ borderRadius: 12 }}
                                    />
                                </Tooltip>
                            </Space>
                        </Col>
                    </Row>

                    {/* Bulk Actions Bar */}
                    {selectedRowKeys.length > 0 && (
                        <div style={{ 
                            marginTop: 16,
                            padding: '12px 16px',
                            background: '#f0f9ff',
                            borderRadius: 12,
                            border: '1px solid #91d5ff',
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center'
                        }}>
                            <Space>
                                <Badge count={selectedRowKeys.length} style={{ backgroundColor: '#1890ff' }} />
                                <Text strong>{selectedRowKeys.length} issue(s) selected</Text>
                                <Button
                                    type="link"
                                    size="small"
                                    onClick={() => {
                                        setSelectedRowKeys([]);
                                        setSelectAll(false);
                                    }}
                                >
                                    Clear selection
                                </Button>
                            </Space>
                            <Space>
                                <Dropdown overlay={bulkActionsMenu} trigger={['click']}>
                                    <Button icon={<MoreOutlined />}>
                                        Bulk Actions
                                    </Button>
                                </Dropdown>
                                <Popconfirm
                                    title="Move selected issues to trash?"
                                    description={`Are you sure you want to move ${selectedRowKeys.length} issue(s) to trash?`}
                                    onConfirm={handleBulkSoftDelete}
                                    okText="Yes"
                                    cancelText="No"
                                >
                                    <Button danger icon={<DeleteOutlined />}>
                                        Delete
                                    </Button>
                                </Popconfirm>
                            </Space>
                        </div>
                    )}
                </Card>
            </motion.div>

            {/* Results Header */}
            <div style={{
                padding: '16px 20px',
                background: '#ffffff',
                borderTop: '1px solid #f0f0f0',
                borderBottom: '1px solid #f0f0f0',
                borderRadius: '12px 12px 0 0',
                marginTop: 8,
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center'
            }}>
                <Space>
                    <Text strong style={{ fontSize: 16 }}>
                        <InboxOutlined style={{ marginRight: 8, color: '#1890ff' }} />
                        Issue List
                    </Text>
                    <Badge
                        count={issues.length}
                        showZero
                        style={{
                            backgroundColor: '#52c41a',
                            fontSize: 12,
                            fontWeight: 'bold'
                        }}
                    />
                    <Text type="secondary">
                        of {totalRecords} total issues
                    </Text>
                    {quickFilter !== 'custom' && quickFilter && (
                        <Tag color="blue" style={{ borderRadius: 12 }}>
                            <CalendarOutlined /> {quickFilter === 'today' ? 'Today' : 
                                               quickFilter === 'yesterday' ? 'Yesterday' :
                                               quickFilter === 'thisWeek' ? 'This Week' :
                                               quickFilter === 'thisMonth' ? 'This Month' :
                                               quickFilter === 'lastMonth' ? 'Last Month' :
                                               quickFilter === 'thisQuarter' ? 'This Quarter' :
                                               quickFilter === 'thisYear' ? 'This Year' : quickFilter}
                        </Tag>
                    )}
                </Space>
                <Space>
                    <Button
                        type="primary"
                        icon={<PlusOutlined />}
                        onClick={() => setDrawerVisible(true)}
                        style={{ borderRadius: 12 }}
                    >
                        Create Issue
                    </Button>
                    <Tooltip title="Refresh">
                        <Button 
                            icon={<ReloadOutlined />} 
onClick={() => fetchIssues()}
                            loading={loading}
                            style={{ borderRadius: 12 }}
                        />
                    </Tooltip>
                </Space>
            </div>

            {/* Main Table */}
            <Card 
                bordered={false}
                style={{ 
                    borderRadius: '0 0 20px 20px',
                    boxShadow: '0 4px 12px rgba(0,0,0,0.05)',
                    overflow: 'hidden',
                    borderTop: 'none'
                }}
                bodyStyle={{ padding: 0 }}
            >
                {/* Server-side pagination: the table receives only the current
                    page of rows, so antd's own pagination is disabled and this
                    standalone control drives page / page-size state. */}
                <div style={{ display: 'flex', justifyContent: 'flex-end', padding: '16px 24px' }}>
                    <Pagination
                        current={currentPage}
                        pageSize={pageSize}
                        total={totalRecords}
                        showSizeChanger
                        pageSizeOptions={['10', '20', '30', '50', '100']}
                        onChange={(page, size) => {
                            setCurrentPage(size !== pageSize ? 1 : page);
                            setPageSize(size);
                        }}
                        showTotal={(total, range) => (
                            <Text type="secondary">
                                Showing {range[0]}-{range[1]} of {total} issues
                            </Text>
                        )}
                    />
                </div>
                <Table
                    rowKey="_id"
                    columns={columns}
                    dataSource={issues}
                    loading={loading}
                    pagination={false}
                    onChange={handleTableChange}
                    onRow={(record) => ({
                        onClick: () => handleIssueClick(record),
                        style: { cursor: 'pointer' }
                    })}
                    scroll={{ x: tableScrollWidth, y: 'calc(100vh - 500px)' }}
                    rowClassName={() => 'table-row-hover'}
                    bordered={false}
                />
            </Card>

            {/* Drawer for creating issue */}
            <AddIssueDrawer
                visible={drawerVisible}
                onClose={() => setDrawerVisible(false)}
                onSuccess={handleIssueCreated}
            />

            {/* Drawer for issue details */}
            <IssueDetailsDrawer
                visible={detailsDrawerVisible}
                openInEditMode={drawerOpensInEditMode}
                onClose={() => {
                    setDetailsDrawerVisible(false);
                    setSelectedIssue(null);
                    setDrawerOpensInEditMode(false);
                }}
                issue={selectedIssue}
                onUpdate={() => fetchIssues()}
            />

            <style>{`
                .table-row-hover:hover {
                    background: linear-gradient(90deg, #f0f7ff 0%, #ffffff 100%) !important;
                    transition: all 0.3s ease;
                }
                
                .ant-table-tbody > tr > td {
                    border-bottom: 1px solid #f0f0f0 !important;
                }
                
                .ant-table-thead > tr > th {
                    background: #fafafa !important;
                    font-weight: 600 !important;
                    font-size: 13px !important;
                    color: #1f2937 !important;
                    border-bottom: 2px solid #f0f0f0 !important;
                }
            `}</style>

            {/* Comment Modal */}
            <Modal
                title={
                    <Space>
                        <CommentOutlined style={{ color: '#1890ff' }} />
                        <span>Customer Comments for Issue: {selectedIssueForComment?.issueType || 'Untitled'}</span>
                    </Space>
                }
                open={commentModalVisible}
                onCancel={() => {
                    setCommentModalVisible(false);
                    setSelectedIssueForComment(null);
                    setNewComment('');
                }}
                footer={null}
                width={700}
                style={{ top: 20 }}
                styles={{ body: { maxHeight: 'calc(100vh - 200px)', overflowY: 'auto', padding: '20px' } }}
            >
     

                {/* Comments List */}
                <div>
                    <Title level={5}>
                        <CommentOutlined /> All Customer Comments ({selectedIssueForComment?.description?.length || 0})
                    </Title>
                    <div style={{ maxHeight: 400, overflowY: 'auto' }}>
                        {selectedIssueForComment?.description && selectedIssueForComment.description.length > 0 ? (
                            [...selectedIssueForComment.description]
                                .sort((a, b) => new Date(b.changedAt) - new Date(a.changedAt))
                                .map((comment, index) => (
                                    <Card
                                        key={index}
                                        size="small"
                                        style={{
                                            marginBottom: 12,
                                            borderRadius: 8,
                                            backgroundColor: index === 0 ? '#f0f9ff' : '#ffffff',
                                            border: index === 0 ? '1px solid #91d5ff' : '1px solid #f0f0f0'
                                        }}
                                        styles={{ body: { padding: '12px' } }}
                                    >
                                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                                            <Space>
                                                {index === 0 && (
                                                    <Tag color="blue" style={{ marginLeft: 8 }}>Latest</Tag>
                                                )}
                                            </Space>
                                            <Text type="secondary" style={{ fontSize: 12 }}>
                                                <ClockCircleOutlined style={{ marginRight: 4 }} />
                                                {dayjs(comment.changedAt).format('MMM DD, YYYY · hh:mm A')}
                                            </Text>
                                        </div>
                                        <div style={{ paddingLeft: 32 }}>
                                            <Text>{comment.comment}</Text>
                                        </div>
                                    </Card>
                                ))
                        ) : (
                            <div style={{ textAlign: 'center', padding: '40px 20px', color: '#bfbfbf' }}>
                                <CommentOutlined style={{ fontSize: 48, marginBottom: 16 }} />
                                <div>No comments yet</div>
                                <Text type="secondary" style={{ fontSize: 12 }}>Be the first to add a comment</Text>
                            </div>
                        )}
                    </div>
                </div>
            </Modal>
            {/* Yard Comment Modal */}
<Modal
    title={
        <Space>
            <ShopOutlined style={{ color: '#fa8c16' }} />
            <span>Yard Comments for Issue: {selectedIssueForYardComment?.issueType || 'Untitled'}</span>
        </Space>
    }
    open={yardCommentModalVisible}
    onCancel={() => {
        setYardCommentModalVisible(false);
        setSelectedIssueForYardComment(null);
        setNewYardComment('');
    }}
    footer={null}
    width={700}
    style={{ top: 20 }}
    styles={{ body: { maxHeight: 'calc(100vh - 200px)', overflowY: 'auto', padding: '20px' } }}
>


    {/* Yard Comments List */}
    <div>
        <Title level={5}>
            <ShopOutlined /> All Yard Comments ({selectedIssueForYardComment?.yardDescription?.length || 0})
        </Title>
        <div style={{ maxHeight: 400, overflowY: 'auto' }}>
            {selectedIssueForYardComment?.yardDescription && selectedIssueForYardComment.yardDescription.length > 0 ? (
                [...selectedIssueForYardComment.yardDescription]
                    .sort((a, b) => new Date(b.changedAt) - new Date(a.changedAt))
                    .map((comment, index) => (
                        <Card
                            key={index}
                            size="small"
                            style={{
                                marginBottom: 12,
                                borderRadius: 8,
                                backgroundColor: index === 0 ? '#fff7e6' : '#ffffff',
                                border: index === 0 ? '1px solid #ffd591' : '1px solid #f0f0f0'
                            }}
                            styles={{ body: { padding: '12px' } }}
                        >
                            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                                <Space>
                                    {index === 0 && (
                                        <Tag color="orange" style={{ marginLeft: 8 }}>Latest</Tag>
                                    )}
                                </Space>
                                <Text type="secondary" style={{ fontSize: 12 }}>
                                    <ClockCircleOutlined style={{ marginRight: 4 }} />
                                    {dayjs(comment.changedAt).format('MMM DD, YYYY · hh:mm A')}
                                </Text>
                            </div>
                            <div style={{ paddingLeft: 32 }}>
                                <Text>{comment.comment}</Text>
                            </div>
                        </Card>
                    ))
            ) : (
                <div style={{ textAlign: 'center', padding: '40px 20px', color: '#bfbfbf' }}>
                    <ShopOutlined style={{ fontSize: 48, marginBottom: 16 }} />
                    <div>No yard comments yet</div>
                    <Text type="secondary" style={{ fontSize: 12 }}>Add the first yard comment</Text>
                </div>
            )}
        </div>
    </div>
</Modal>

            {/* Full calculation notes. Whitespace is preserved because these
                notes are usually a worked-out calculation, where the line breaks
                and indentation carry the meaning. */}
            <Modal
                title="Calculation Notes"
                open={!!calcNotesIssue}
                onCancel={handleCloseCalcNotes}
                maskClosable={!calcNotesEditing}
                footer={calcNotesEditing ? [
                    <Button key="cancel" onClick={handleCancelCalcNotes} disabled={calcNotesSaving}>
                        Cancel
                    </Button>,
                    <Button
                        key="save"
                        type="primary"
                        icon={<CheckOutlined />}
                        loading={calcNotesSaving}
                        onClick={handleSaveCalcNotes}
                    >
                        Save
                    </Button>
                ] : [
                    <Button key="close" onClick={handleCloseCalcNotes}>Close</Button>,
                    <Button key="edit" type="primary" icon={<EditOutlined />} onClick={handleEditCalcNotes}>
                        Edit
                    </Button>
                ]}
                width={720}
            >
                {calcNotesIssue && (
                    <>
                        <div style={{ marginBottom: 16, padding: '8px 12px', background: '#fafafa', borderRadius: 6, fontSize: 13 }}>
                            <strong>Issue:</strong> {calcNotesIssue.issueType === 'Other'
                                ? (calcNotesIssue.customIssueType || 'Other')
                                : calcNotesIssue.issueType}
                            {calcNotesIssue.lead?.orderNumber ? ` · ${calcNotesIssue.lead.orderNumber}` : ''}
                        </div>

                        {calcNotesEditing ? (
                            <>
                                {/* Same monospace face and line height as the
                                    read-only view, so the layout of a calculation
                                    does not shift when you start editing it. */}
                                <Input.TextArea
                                    value={calcNotesDraft}
                                    onChange={(e) => setCalcNotesDraft(e.target.value)}
                                    autoSize={{ minRows: 10, maxRows: 20 }}
                                    placeholder="Enter the calculation notes…"
                                    style={{
                                        fontFamily: "'SFMono-Regular', Consolas, 'Liberation Mono', Menlo, monospace",
                                        fontSize: 13,
                                        lineHeight: 1.7
                                    }}
                                />
                                <Text type="secondary" style={{ fontSize: 12, display: 'block', marginTop: 8 }}>
                                    Line breaks and indentation are kept exactly as typed. Every change is
                                    recorded in this field's history.
                                </Text>
                            </>
                        ) : (
                            <div
                                style={{
                                    whiteSpace: 'pre-wrap',
                                    wordBreak: 'break-word',
                                    fontFamily: "'SFMono-Regular', Consolas, 'Liberation Mono', Menlo, monospace",
                                    fontSize: 13,
                                    lineHeight: 1.7,
                                    background: '#fafafa',
                                    border: '1px solid #f0f0f0',
                                    borderRadius: 6,
                                    padding: '14px 16px',
                                    maxHeight: '55vh',
                                    overflowY: 'auto'
                                }}
                            >
                                {calcNotesIssue.calculationNotes}
                            </div>
                        )}
                    </>
                )}
            </Modal>

            {/* Field History Modal — shared by the Issue Created Date, Customer
                Comments, Yard Comments and Status History buttons. */}
            <Modal
                title={(FIELD_HISTORY_CONFIG[historyField] || {}).title || 'History'}
                open={!!historyField}
                onCancel={handleCloseHistory}
                footer={[<Button key="close" onClick={handleCloseHistory}>Close</Button>]}
                width={620}
            >
                {historyIssue && (
                    <div style={{ marginBottom: 16, padding: '8px 12px', background: '#fafafa', borderRadius: 6, fontSize: 13 }}>
                        <strong>Issue:</strong> {historyIssue.issueType === 'Other'
                            ? (historyIssue.customIssueType || 'Other')
                            : historyIssue.issueType}
                        {historyIssue.lead?.orderNumber ? ` · ${historyIssue.lead.orderNumber}` : ''}
                    </div>
                )}

                {historyLoading ? (
                    <div style={{ textAlign: 'center', padding: 40 }}>
                        <Text type="secondary">Loading history…</Text>
                    </div>
                ) : historyEntries.length === 0 ? (
                    <div style={{ textAlign: 'center', padding: 40, color: '#999' }}>
                        No {(FIELD_HISTORY_CONFIG[historyField] || {}).noun || 'change'} history recorded for this issue.
                    </div>
                ) : (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                        {/* Oldest first: these logs are appended to, so their natural
                            order already reads chronologically. */}
                        {historyEntries.map((entry, index) => {
                            const config = FIELD_HISTORY_CONFIG[historyField] || {};
                            const isCreation = entry.value_from === null || entry.value_from === undefined;
                            const show = (value) => {
                                if (value === null || value === undefined || value === '') return '(empty)';
                                return config.isDate ? dayjs(value).format('MM-DD-YYYY hh:mm A') : String(value);
                            };

                            return (
                                <div
                                    key={entry._id || index}
                                    style={{
                                        borderLeft: `3px solid ${isCreation ? '#52c41a' : '#1890ff'}`,
                                        padding: '8px 12px',
                                        background: '#fafafa',
                                        borderRadius: 4
                                    }}
                                >
                                    <div style={{ fontWeight: 500, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                                        {isCreation ? `Created — ${show(entry.value_to)}` : show(entry.value_to)}
                                    </div>

                                    {!isCreation && (
                                        <div style={{ fontSize: 12, color: '#999', marginTop: 2, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                                            previously: {show(entry.value_from)}
                                        </div>
                                    )}

                                    <div style={{ fontSize: 12, color: '#666', marginTop: 4 }}>
                                        by {formatHistoryActor(entry)}
                                        {entry.changed_at ? ` on ${dayjs(entry.changed_at).format('MM-DD-YYYY hh:mm A')}` : ''}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )}
            </Modal>
        </div>
    );
};

export default IssueTracking;
